import axiosInstance from '../api/axiosInstance';

/**
 * dataEntryService — All Data Entry API calls
 *
 * Relative path: /data-entry (REACT_APP_API_URL handles /api)
 *
 * Categories:
 *   3.1 Business Data APIs  (5 endpoints)
 *   3.2 Quote Document       (4 endpoints)
 *   3.3 Draft BL Document    (4 endpoints)
 *   3.4 Rollover Document    (4 endpoints)
 *   3.5 Copy Documents       (5 endpoints — multiple upload)
 *   3.6 Container Image      (4 endpoints — per containerNo)
 */

// ─── 3.1 Business Data APIs ───────────────────────────────────────────────────

/** GET /data-entry — paginated list */
export const getDataEntries = (params) =>
  axiosInstance.get('/data-entry', { params }).then((res) => res.data);

/** GET /data-entry/{id} */
export const getDataEntryById = (id) => {
  if (!id || id === 'new') return Promise.resolve(null);
  return axiosInstance.get(`/data-entry/${id}`).then((res) => res.data);
};

/** POST /data-entry */
export const createDataEntry = (data) =>
  axiosInstance.post('/data-entry', data).then((res) => res.data);

/** PUT /data-entry/{id} */
export const updateDataEntry = (id, data) =>
  axiosInstance.put(`/data-entry/${id}`, data).then((res) => res.data);

/** DELETE /data-entry/{id} */
export const deleteDataEntry = (id) =>
  axiosInstance.delete(`/data-entry/${id}`).then((res) => res.data);

// Helper to ensure payload is binary FormData
const toFormData = (data, defaultKey = 'file') => {
  if (data instanceof FormData) return data;
  const formData = new FormData();
  if (!data) return formData;

  if (data instanceof File || data instanceof Blob) {
    formData.append(defaultKey, data);
    if (defaultKey !== 'file') formData.append('file', data);
  } else if (Array.isArray(data)) {
    data.forEach((item) => {
      const fileObj = item instanceof File || item instanceof Blob ? item : item?.file;
      if (fileObj) {
        formData.append(defaultKey, fileObj);
        if (defaultKey !== 'file') formData.append('file', fileObj);
      }
    });
  } else if (typeof data === 'object') {
    if (data.file && (data.file instanceof File || data.file instanceof Blob)) {
      formData.append('file', data.file);
      formData.append('files', data.file);
    } else if (data.files && Array.isArray(data.files)) {
      data.files.forEach((f) => {
        formData.append('files', f);
        formData.append('file', f);
      });
    } else {
      Object.entries(data).forEach(([key, value]) => {
        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((v) => formData.append(key, v));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
    }
  }
  return formData;
};

const multipartConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
};

// ─── 3.2 Quote Document APIs ──────────────────────────────────────────────────

/** GET /data-entry/quote-document?fileName=&blNo= */
export const getQuoteDocument = (fileName, blNo) =>
  axiosInstance
    .get('/data-entry/quote-document', { params: { fileName, blNo }, suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

/** POST /data-entry/quote-document?fileName=&blNo= */
export const uploadQuoteDocument = (fileName, blNo, data) =>
  axiosInstance
    .post('/data-entry/quote-document', toFormData(data), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** PUT /data-entry/quote-document?fileName=&blNo= */
export const updateQuoteDocument = (fileName, blNo, data) =>
  axiosInstance
    .put('/data-entry/quote-document', toFormData(data), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** DELETE /data-entry/quote-document?fileName=&blNo= */
export const deleteQuoteDocument = (fileName, blNo) =>
  axiosInstance.delete('/data-entry/quote-document', { params: { fileName, blNo } }).then((res) => res.data);

// ─── 3.3 Draft BL Document APIs ───────────────────────────────────────────────

/** GET /data-entry/draft-bl-document?fileName=&blNo= */
export const getDraftBlDocument = (fileName, blNo) =>
  axiosInstance
    .get('/data-entry/draft-bl-document', { params: { fileName, blNo }, suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

/** POST /data-entry/draft-bl-document?fileName=&blNo= */
export const uploadDraftBlDocument = (fileName, blNo, data) =>
  axiosInstance
    .post('/data-entry/draft-bl-document', toFormData(data), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** PUT /data-entry/draft-bl-document?fileName=&blNo= */
export const updateDraftBlDocument = (fileName, blNo, data) =>
  axiosInstance
    .put('/data-entry/draft-bl-document', toFormData(data), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** DELETE /data-entry/draft-bl-document?fileName=&blNo= */
export const deleteDraftBlDocument = (fileName, blNo) =>
  axiosInstance.delete('/data-entry/draft-bl-document', { params: { fileName, blNo } }).then((res) => res.data);

// ─── 3.4 Rollover Document APIs ───────────────────────────────────────────────

export const getRolloverDocument = (id) =>
  axiosInstance
    .get(`/data-entry/${id}/rollover-document`, { suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

export const uploadRolloverDocument = (id, data) =>
  axiosInstance
    .post(`/data-entry/${id}/rollover-document`, toFormData(data), multipartConfig)
    .then((res) => res.data);

export const updateRolloverDocument = (id, data) =>
  axiosInstance
    .put(`/data-entry/${id}/rollover-document`, toFormData(data), multipartConfig)
    .then((res) => res.data);

export const deleteRolloverDocument = (id) =>
  axiosInstance.delete(`/data-entry/${id}/rollover-document`).then((res) => res.data);

// ─── 3.5 Copy Documents APIs (Multiple Upload) ────────────────────────────────

/** GET /data-entry/copy-documents?fileName=&blNo= — get copy documents by params */
export const getCopyDocuments = (fileName, blNo) =>
  axiosInstance
    .get('/data-entry/copy-documents', { params: { fileName: fileName || '', blNo: blNo || '' }, suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return [];
      throw err;
    });

export const getCopyDocumentsByParams = getCopyDocuments;

/** GET /data-entry/{id}/copy-documents/{documentId} */
export const getCopyDocumentById = (id, documentId) =>
  axiosInstance
    .get(`/data-entry/${id}/copy-documents/${documentId}`, { suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

/** POST /data-entry/copy-documents?fileName=&blNo= */
export const uploadCopyDocument = (fileName, blNo, data) =>
  axiosInstance
    .post('/data-entry/copy-documents', toFormData(data, 'files'), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** PUT /data-entry/copy-documents?fileName=&blNo= */
export const updateCopyDocument = (fileName, blNo, data) =>
  axiosInstance
    .put('/data-entry/copy-documents', toFormData(data, 'files'), {
      params: { fileName, blNo },
      ...multipartConfig,
    })
    .then((res) => res.data);

/** DELETE /data-entry/copy-documents/{documentId} */
export const deleteCopyDocument = (documentId) =>
  axiosInstance.delete(`/data-entry/copy-documents/${documentId}`).then((res) => res.data);

// ─── 3.6 Container Image APIs ─────────────────────────────────────────────────

/** GET /data-entry/container-image?fileName=&blNo=&containerNo= (containerNo optional) */
export const getContainerImage = (fileName, blNo, containerNo) => {
  const params = { fileName: fileName || '' };
  if (blNo) params.blNo = blNo;
  if (containerNo) params.containerNo = containerNo;
  return axiosInstance
    .get('/data-entry/container-image', { params, suppress404Toast: true })
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return [];
      throw err;
    });
};

/** GET /data-entry/container-image?fileName=&blNo=&containerNo= (alias) */
export const getContainerImageByParams = getContainerImage;

/** POST /data-entry/container-image?fileName=&blNo=&containerNo= (containerNo optional) */
export const uploadContainerImage = (fileName, blNo, containerNo, data) => {
  const params = { fileName: fileName || '' };
  if (blNo) params.blNo = blNo;
  if (containerNo) params.containerNo = containerNo;
  return axiosInstance
    .post('/data-entry/container-image', toFormData(data, 'files'), {
      params,
      ...multipartConfig,
    })
    .then((res) => res.data);
};

/** DELETE /data-entry/container-image/{id} */
export const deleteContainerImage = (id) =>
  axiosInstance.delete(`/data-entry/container-image/${id}`).then((res) => res.data);

